import { CSSObject } from '@emotion/react'
import { Fragment, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import AdhocAttackMetrics from 'components/attacks/details/AdhocAttackMetrics'
import AttackMetricsChartTeaser from 'components/attacks/details/AttackMetricsChartTeaser'
import DetailPanel from 'components/attacks/details/DetailPanel'
import ExecutionPanel from 'components/attacks/details/ExecutionPanel'
import InfraAttackResultsSection from 'components/attacks/details/InfraAttackResultsSection'
import OnboardingAttackModal from 'components/attacks/details/OnboardingAttackModal'
import TargetingPanel from 'components/attacks/details/TargetingPanel'
import { isAttackInCompleteState } from 'components/attacks/utils/attack-stages'
import {
  convertAttackToScenarioState,
  copyAttackForReuse
} from 'components/attacks/utils/attacks'
import { generateRerunAttackLinkTo } from 'components/attacks/utils/rerun'
import ContentErrorMessage from 'components/common/ContentErrorMessage'
import Section from 'components/common/Section'
import useAgent from 'hooks/use-agent'
import useAttackMetricsStatus from 'hooks/use-attack-metrics-status'
import useAuth from 'hooks/use-auth'
import useMetadata from 'hooks/use-metadata'
import theme from 'theme'
import { InfraAttack } from 'types/api/attacks/details/infra-attack'
import { attackSupportsMetrics } from 'utils/attack-metrics'

import CreateScenarioFromAttack from './CreateScenarioFromAttack'

export type AttackMetricsGraphProps = {
  css?: CSSObject
}

export const DataVisualizationChartDetail: React.FC<{
  attack: InfraAttack
  isScenario: boolean
  attackMetricsGraphProps: AttackMetricsGraphProps
}> = ({ attack, isScenario, attackMetricsGraphProps }) => {
  const { isOptIn, canUpdateCompanyAttackVisualizations } =
    useAttackMetricsStatus()

  // Prevent loading the chart if we know there's no possibility of metrics for this attack type
  if (
    !attackSupportsMetrics(
      attack?.infra_command?.commandType,
      attack?.target_type
    )
  ) {
    return null
  }

  if (isOptIn) {
    return (
      <AdhocAttackMetrics
        attack={attack}
        darkMode={isScenario}
        {...attackMetricsGraphProps}
      />
    )
  } else if (canUpdateCompanyAttackVisualizations) {
    return <AttackMetricsChartTeaser darkMode={isScenario} />
  } else {
    return null
  }
}

export const InfraAttackContents: React.FC<{
  attackId?: string
  referenceAttack?: InfraAttack
  displayFirstAttackModal?: boolean
  isScenario?: boolean
  attackMetricsGraphProps?: AttackMetricsGraphProps
}> = ({
  attackId,
  referenceAttack,
  isScenario = false,
  displayFirstAttackModal = false,
  attackMetricsGraphProps
}) => {
  const { currentTeamId } = useAuth()
  const history = useHistory()
  const [isAttackPollingEnabled, setIsAttackPollingEnabled] = useState(
    !isAttackInCompleteState(referenceAttack)
  )
  const [isFirstAttackModalShown, setIsFirstAttackModalShown] = useState(false)
  const { metadata } = useMetadata()

  const {
    data: [attack, executions],
    isLoading,
    error: [attackError],
    fetchSuccessCount,
    doFetch
  } = useAgent(
    [
      `/attacks/${attackId}?teamId=${currentTeamId}`,
      `executions?taskId=${attackId}&teamId=${currentTeamId}`
    ],
    [referenceAttack, []],
    {
      shouldCache: true,
      interval: isAttackPollingEnabled ? 4000 : null
    }
  )

  const isServiceBasedAttack = typeof attack?.service_id === 'string'
  const isExact =
    attack?.infra_target?.strategy?.type === 'Exact' ||
    attack?.attack_configuration?.targets?.some(
      (target) =>
        target.targetingStrategy.ids &&
        target.targetingStrategy.type !== 'Kubernetes'
    )

  // Stop polling for the attack data if its in completed stage
  useEffect(() => {
    if (isAttackPollingEnabled && attack && isAttackInCompleteState(attack)) {
      setIsAttackPollingEnabled(false)
      if (attack.create_source === 'Onboarding') {
        setIsFirstAttackModalShown(true)
      }
    }
  }, [attack, isAttackPollingEnabled])

  const handleSubmit = (name, description, hypothesis) => {
    // we specifically want to try and use `attack` first here
    // because it will have the dynamically_resolved_ips, which are needed in some cases
    // `executions` does not have this data
    const reusedAttack = copyAttackForReuse(attack ?? executions[0], metadata)

    history.push({
      pathname: '/scenarios/new/hosts',
      state: {
        ...convertAttackToScenarioState(
          reusedAttack,
          name,
          description,
          hypothesis
        )
      }
    })
  }

  const rerunAttackLink = generateRerunAttackLinkTo(attack, metadata)

  return (
    <Section
      data-test-attack-contents
      css={{
        display: 'grid',
        gap: theme.space[4],
        gridAutoRows: 'max-content'
      }}
      isLoading={isLoading}
    >
      {attackError && fetchSuccessCount < 1 ? (
        <ContentErrorMessage
          data-test-content-error-message
          destination="/attacks"
          fetchError={attackError}
          returnText="Return to Attacks"
        />
      ) : (
        <Fragment>
          <DataVisualizationChartDetail
            attack={attack}
            attackMetricsGraphProps={attackMetricsGraphProps}
            isScenario={isScenario}
          />
          {!isServiceBasedAttack && !isScenario && (
            <CreateScenarioFromAttack
              isShowCreateScenario={!isExact}
              rerunLink={rerunAttackLink}
              onSubmit={handleSubmit}
            />
          )}
          <Section
            css={{
              display: 'grid',
              gap: theme.space[4]
            }}
          >
            <DetailPanel
              attack={attack}
              doFetch={doFetch}
              isScenario={isScenario}
              metadata={metadata}
              rerunLinkTo={rerunAttackLink}
              showRerunButton={false}
            />
            <TargetingPanel attack={attack} />
            <InfraAttackResultsSection attack={attack} doFetch={doFetch} />
            <ExecutionPanel attackId={attack?.guid} executions={executions} />
          </Section>
        </Fragment>
      )}
      <OnboardingAttackModal
        open={displayFirstAttackModal && isFirstAttackModalShown}
        onCancel={() => {
          setIsFirstAttackModalShown(false)
        }}
      />
    </Section>
  )
}
